import { useEffect, useRef, useState } from 'react';
import { Box, Divider, Menu, MenuItem, TextField, Tooltip } from '@mui/material';
import ConfirmationDialog from 'components/Dialogs/ConfirmationDialog';
import { AIGradientButton } from '../../../../DocumentGenerationJobs/components/AIGradient';
import { CAP_TABLE_CLEAR_COPY } from '../../utilities/capTableClear';
import type { CapTableResetMode } from '../../utilities/capTableReset';
import { CAP_TABLE_RESET_COPY } from '../../utilities/capTableReset';

interface AiProcessingMenuProps {
  onGenerateCapTable: () => void;
  /** Omit to leave Create Citations out of the menu entirely. */
  onCreateCitations?: () => void;
  onConfirmReset: (mode: CapTableResetMode) => boolean | Promise<boolean>;
  /** Whether this user may clear anything: staff with edit rights on the cap table. */
  canClear: boolean;
  /** Cap table the whole-table clear targets; the action waits until it is known. */
  capTableId?: number | null;
  /** Names that cap table in the success toast, captured when the dialog opens. */
  capTableLabel?: string;
  onClearCapTable?: (capTableId: number, capTableLabel: string) => void | Promise<void>;
  generateDisabled?: boolean;
  /** When set, Create Cap Table Citations is disabled and the text explains why. */
  createCitationsDisabledReason?: string | null;
  onMenuOpenChange?: (open: boolean) => void;
}

const RESET_ITEMS: CapTableResetMode[] = ['start_fresh', 'clear_ai_evidence', 'clear_pending'];

const AiProcessingMenu = ({
  onGenerateCapTable,
  onCreateCitations,
  onConfirmReset,
  canClear,
  capTableId = null,
  capTableLabel = '',
  onClearCapTable,
  generateDisabled = false,
  createCitationsDisabledReason = null,
  onMenuOpenChange,
}: AiProcessingMenuProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [pendingMode, setPendingMode] = useState<CapTableResetMode | null>(null);
  const [confirming, setConfirming] = useState(false);
  const closeConfirmDialog = useRef(true);
  const [clearTarget, setClearTarget] = useState<{ id: number; label: string } | null>(null);
  const [clearPhrase, setClearPhrase] = useState('');
  const [clearing, setClearing] = useState(false);
  const clearingRef = useRef(false);

  const closeMenu = () => {
    setMenuAnchor(null);
    onMenuOpenChange?.(false);
  };

  const handleConfirm = async () => {
    if (!pendingMode || confirming) {
      closeConfirmDialog.current = false;
      return;
    }
    setConfirming(true);
    try {
      const ok = await onConfirmReset(pendingMode);
      closeConfirmDialog.current = ok;
      if (ok) {
        setPendingMode(null);
      }
    } finally {
      setConfirming(false);
    }
  };

  // Abandon the destructive confirmation the moment the cap table it was opened for
  // stops being the selected one, including when the selection empties.
  useEffect(() => {
    if (clearTarget && clearTarget.id !== capTableId) {
      setClearTarget(null);
      setClearPhrase('');
    }
  }, [capTableId, clearTarget]);

  // Every close path - Cancel, Escape, backdrop, a finished request - forgets the typed phrase.
  const closeClearDialog = () => {
    if (clearingRef.current) {
      return;
    }
    setClearTarget(null);
    setClearPhrase('');
  };

  const handleClearConfirm = async () => {
    if (clearingRef.current || !clearTarget) {
      return;
    }
    clearingRef.current = true;
    setClearing(true);
    try {
      await onClearCapTable?.(clearTarget.id, clearTarget.label);
    } finally {
      clearingRef.current = false;
      setClearing(false);
    }
  };

  const copy = pendingMode ? CAP_TABLE_RESET_COPY[pendingMode] : null;

  return (
    <>
      <AIGradientButton
        size="small"
        data-testid="ai-processing-menu-button"
        aria-haspopup="true"
        aria-expanded={Boolean(menuAnchor)}
        onClick={event => {
          setMenuAnchor(event.currentTarget);
          onMenuOpenChange?.(true);
        }}>
        AI Processing
      </AIGradientButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        MenuListProps={{ 'aria-label': 'AI Processing' }}>
        <MenuItem
          disabled={generateDisabled}
          onClick={() => {
            closeMenu();
            onGenerateCapTable();
          }}>
          Generate Cap Table
        </MenuItem>
        {/* The span keeps the tooltip alive while the disabled item swallows pointer events. */}
        {onCreateCitations && (
          <Tooltip disableInteractive placement="left" title={createCitationsDisabledReason ?? ''}>
            <span>
              <MenuItem
                disabled={Boolean(createCitationsDisabledReason)}
                onClick={() => {
                  closeMenu();
                  onCreateCitations();
                }}>
                Create Cap Table Citations
              </MenuItem>
            </span>
          </Tooltip>
        )}
        {canClear &&
          RESET_ITEMS.map(mode => (
            <MenuItem
              key={mode}
              onClick={() => {
                closeMenu();
                setPendingMode(mode);
              }}>
              {CAP_TABLE_RESET_COPY[mode].title}
            </MenuItem>
          ))}
        {canClear && <Divider />}
        {canClear && (
          <MenuItem
            data-testid="clear-entire-cap-table"
            disabled={capTableId === null}
            sx={{ color: 'error.main' }}
            onClick={() => {
              closeMenu();
              setClearPhrase('');
              setClearTarget(capTableId === null ? null : { id: capTableId, label: capTableLabel });
            }}>
            {CAP_TABLE_CLEAR_COPY.menuLabel}
          </MenuItem>
        )}
      </Menu>
      <ConfirmationDialog
        open={Boolean(pendingMode)}
        title={copy?.title}
        onClose={() => {
          if (closeConfirmDialog.current) {
            setPendingMode(null);
          }
          closeConfirmDialog.current = true;
        }}
        actions={[
          { label: 'Cancel', variant: 'text', disabled: confirming },
          { label: 'Confirm', variant: 'contained', disabled: confirming, callback: handleConfirm },
        ]}>
        {copy?.remains}
      </ConfirmationDialog>
      <ConfirmationDialog
        open={Boolean(clearTarget)}
        title={CAP_TABLE_CLEAR_COPY.title}
        isLoading={clearing}
        onClose={closeClearDialog}
        actions={[
          { label: 'Cancel', variant: 'text', disabled: clearing },
          {
            label: CAP_TABLE_CLEAR_COPY.confirmLabel,
            variant: 'contained',
            type: 'danger',
            disabled: clearing || clearPhrase !== CAP_TABLE_CLEAR_COPY.confirmPhrase,
            callback: handleClearConfirm,
          },
        ]}>
        <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
          {CAP_TABLE_CLEAR_COPY.points.map(point => (
            <li key={point}>{point}</li>
          ))}
        </Box>
        <TextField
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          label={CAP_TABLE_CLEAR_COPY.confirmPrompt}
          value={clearPhrase}
          disabled={clearing}
          onChange={event => setClearPhrase(event.target.value)}
        />
      </ConfirmationDialog>
    </>
  );
};

export default AiProcessingMenu;
